import { useMemo } from "react";
import { useSovLineFinancials, type SovLineFinancials } from "@/hooks/useSovLineFinancials";
import { useAlertThresholds } from "@/hooks/useAlertThresholds";
import type { AlertSeverity } from "@/hooks/useProjectAlerts";

export interface SovLineAlert {
  ruleId: string;
  severity: AlertSeverity;
  title: string;
  explanation: string;
  metric: number;
}

export const useSovLineAlerts = (contractId: string | undefined) => {
  const { data: financialsMap, isLoading } = useSovLineFinancials(contractId);
  const { getThreshold } = useAlertThresholds();

  const alertMap = useMemo(() => {
    if (!financialsMap) return new Map<string, SovLineAlert[]>();

    const map = new Map<string, SovLineAlert[]>();
    const floorThresh = getThreshold("sov_line_margin_floor");
    const aveThresh = getThreshold("actual_vs_estimate_pct");

    financialsMap.forEach((fin, lineId) => {
      const alerts: SovLineAlert[] = [];

      // Committed margin below floor
      if (floorThresh.active && fin.estimated_revenue > 0) {
        if (fin.committed_margin_pct < floorThresh.critical) {
          alerts.push({
            ruleId: "committed_margin_floor",
            severity: "critical",
            title: "Committed margin critical",
            explanation: `Committed margin is ${fin.committed_margin_pct.toFixed(1)}% (below ${floorThresh.critical}% floor).`,
            metric: fin.committed_margin_pct,
          });
        } else if (fin.committed_margin_pct < floorThresh.warning) {
          alerts.push({
            ruleId: "committed_margin_floor",
            severity: "warning",
            title: "Committed margin low",
            explanation: `Committed margin is ${fin.committed_margin_pct.toFixed(1)}% (below ${floorThresh.warning}% threshold).`,
            metric: fin.committed_margin_pct,
          });
        }
      }

      // Actual margin below floor
      if (floorThresh.active && fin.actual_cost > 0 && fin.estimated_revenue > 0) {
        if (fin.actual_margin_pct < floorThresh.critical) {
          alerts.push({
            ruleId: "actual_margin_floor",
            severity: "critical",
            title: "Actual margin critical",
            explanation: `Actual margin is ${fin.actual_margin_pct.toFixed(1)}% (below ${floorThresh.critical}% floor).`,
            metric: fin.actual_margin_pct,
          });
        } else if (fin.actual_margin_pct < floorThresh.warning) {
          alerts.push({
            ruleId: "actual_margin_floor",
            severity: "warning",
            title: "Actual margin low",
            explanation: `Actual margin is ${fin.actual_margin_pct.toFixed(1)}% (below ${floorThresh.warning}% threshold).`,
            metric: fin.actual_margin_pct,
          });
        }
      }

      // Override exposure present
      if (fin.has_override && fin.override_exposure > 0) {
        alerts.push({
          ruleId: "override_present",
          severity: "warning",
          title: "Rate override active",
          explanation: `Override exposure of $${fin.override_exposure.toLocaleString("en-US", { maximumFractionDigits: 0 })} on this line.`,
          metric: fin.override_exposure,
        });
      }

      // Actual cost exceeding estimate
      if (aveThresh.active && fin.estimated_cost > 0 && fin.actual_cost > 0) {
        const pct = (fin.actual_cost / fin.estimated_cost) * 100;
        if (pct >= aveThresh.critical) {
          alerts.push({
            ruleId: "actual_vs_estimate",
            severity: "critical",
            title: "Actual cost exceeds estimate",
            explanation: `Actual cost is ${pct.toFixed(0)}% of estimated cost (${aveThresh.critical}% threshold).`,
            metric: pct,
          });
        } else if (pct >= aveThresh.warning) {
          alerts.push({
            ruleId: "actual_vs_estimate",
            severity: "warning",
            title: "Actual cost trending high",
            explanation: `Actual cost is ${pct.toFixed(0)}% of estimated cost (${aveThresh.warning}% threshold).`,
            metric: pct,
          });
        }
      }

      if (alerts.length > 0) {
        map.set(lineId, alerts);
      }
    });

    return map;
  }, [financialsMap, getThreshold]);

  return { alertMap, isLoading };
};
